import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Play, Pause, Heart, Bookmark, Share2, ThumbsUp, Lock, Crown,
  BookMarked, Briefcase, BarChart3, ShieldCheck, MessagesSquare, FolderLock,
  CircleDollarSign, UserRound, FolderOpen, BadgeCheck, Star, FileText, Download, Flame,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PitchFlipCard from "@/components/PitchFlipCard";

const TABS = [
  { key: "story", label: "Story", icon: BookMarked, locked: false },
  { key: "business", label: "Business", icon: Briefcase, locked: true },
  { key: "traction", label: "Traction", icon: BarChart3, locked: true },
  { key: "trust", label: "Trust", icon: ShieldCheck, locked: true },
  { key: "qna", label: "Q&A", icon: MessagesSquare, locked: true },
  { key: "dataroom", label: "Data Room", icon: FolderLock, locked: true },
];

const PremiumLock = ({ label }) => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const unlock = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/users/upgrade");
      await refreshUser();
      toast.success("Premium unlocked! (demo mode — payments coming soon)");
    } catch (e) {
      toast.error("Upgrade failed. Please try again.");
    }
  };
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10" data-testid="premium-lock">
      <div className="p-10 blur-sm select-none opacity-40 space-y-4" aria-hidden="true">
        {[90, 70, 85, 60, 75].map((w, i) => (
          <div key={i} className="h-4 rounded bg-white/15" style={{ width: `${w}%` }} />
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-[2px] px-6">
        <span className="w-14 h-14 rounded-2xl bg-[#F5B301]/10 border border-[#F5B301]/40 flex items-center justify-center mb-4">
          <Lock size={22} className="text-[#F5B301]" />
        </span>
        <p className="font-heading font-semibold text-white text-lg">{label} is for Premium members</p>
        <p className="text-sm text-zinc-400 mt-1 text-center max-w-sm">Unlock the full deck, financials, trust report, founder Q&A and data room.</p>
        <button
          data-testid="unlock-premium-btn"
          onClick={unlock}
          className="mt-5 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors"
        >
          <Crown size={15} /> {user ? "Unlock Premium" : "Login to Unlock"}
        </button>
        <Link to="/pricing" className="mt-3 text-xs text-zinc-500 hover:text-[#F5B301]">See what Premium includes →</Link>
      </div>
    </div>
  );
};

const SectionBlock = ({ title, children }) => (
  <div className="text-left">
    <p className="text-[11px] uppercase tracking-widest text-[#F5B301] font-semibold mb-2">{title}</p>
    <p className="text-sm text-zinc-300 leading-relaxed">{children}</p>
  </div>
);

export default function PitchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, savedIds, toggleSave } = useAuth();
  const [pitch, setPitch] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("story");
  const [chapter, setChapter] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [counts, setCounts] = useState(null);
  const [reacted, setReacted] = useState({});
  const videoRef = useRef(null);

  const premium = !!user && (user.is_premium || user.role === "admin");
  const isSaved = savedIds.has(id);

  useEffect(() => {
    api.get(`/pitches/${id}`)
      .then((r) => { setPitch(r.data); setCounts({ hearts: r.data.hearts, likes: r.data.likes, dollars: r.data.dollars }); })
      .catch(() => setNotFound(true));
    api.get(`/pitches/${id}/similar`).then((r) => setSimilar(r.data)).catch(() => {});
    setTab("story");
    setChapter(0);
    setPlaying(false);
    setReacted({});
    window.scrollTo(0, 0);
  }, [id]);

  if (notFound) return <main className="pt-40 pb-24 text-center min-h-screen"><p className="text-zinc-400">Pitch not found.</p><Link to="/pitches" className="text-[#F5B301] text-sm hover:underline">Browse pitches →</Link></main>;
  if (!pitch) return <main className="pt-40 pb-24 text-center text-zinc-500 text-sm min-h-screen">Loading opportunity…</main>;

  const d = pitch.details;
  const current = d.chapters[chapter];

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {
        setPlaying(false);
        toast.error("This video is unavailable right now. Please try again later.");
      });
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const selectChapter = (i) => {
    setChapter(i);
    setPlaying(false);
    if (videoRef.current) videoRef.current.load();
  };

  const engage = async (type, field) => {
    if (reacted[type]) return;
    setReacted((r) => ({ ...r, [type]: true }));
    setCounts((c) => ({ ...c, [field]: c[field] + 1 }));
    try { await api.post(`/pitches/${id}/engage`, { type }); } catch (e) { /* silent */ }
  };

  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); } catch (e) { /* blocked */ }
    api.post(`/pitches/${id}/engage`, { type: "share" }).catch(() => {});
    toast.success(`Link to ${pitch.company} copied!`);
  };

  const expressInterest = async () => {
    if (!user) { toast.info("Login to express capital interest."); navigate("/login"); return; }
    try {
      await api.post(`/pitches/${id}/interest`);
      toast.success(`Capital interest registered — ${pitch.founder} will be notified.`);
    } catch (e) {
      toast.error("Could not register interest. Please try again.");
    }
  };

  const onBookmark = async () => {
    if (!user) { navigate("/login"); return; }
    const saved = await toggleSave(id);
    toast.success(saved ? "Added to your watchlist" : "Removed from watchlist");
  };

  const railBtn = "flex flex-col items-center gap-1.5 text-[11px] text-zinc-400 hover:text-[#F5B301] transition-colors";

  return (
    <main className="relative pt-16 pb-24 min-h-screen" data-testid="pitch-detail-page">
      <div className="noise-overlay" />
      {/* HERO */}
      <section className="relative">
        <div className="relative w-full aspect-video max-h-[72vh] overflow-hidden bg-black">
          <video
            ref={videoRef}
            key={current.video_url}
            src={current.video_url}
            poster={pitch.thumbnail}
            className="w-full h-full object-cover"
            onEnded={() => setPlaying(false)}
            onError={() => setPlaying(false)}
            playsInline
            preload="none"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/60 pointer-events-none" />
          {!playing && (
            <button data-testid="detail-play-btn" onClick={togglePlay} aria-label="Play video" className="absolute inset-0 flex items-center justify-center group">
              <span className="w-20 h-20 rounded-full border-2 border-[#F5B301] bg-black/40 backdrop-blur-md flex items-center justify-center transition-transform group-hover:scale-110">
                <Play size={26} className="text-[#F5B301] ml-1" fill="#F5B301" />
              </span>
            </button>
          )}
          {playing && (
            <button onClick={togglePlay} aria-label="Pause" className="absolute bottom-5 left-5 p-2.5 rounded-full bg-black/60 text-white z-10">
              <Pause size={16} />
            </button>
          )}
          <div className="absolute bottom-8 left-6 sm:left-12 max-w-lg text-left pointer-events-none hidden sm:block">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#F5B301] font-semibold">One Opportunity. One Story.</p>
            <h1 className="mt-2 text-3xl lg:text-4xl font-bold text-white leading-tight">Watch. Understand. Invest with Confidence.</h1>
          </div>
          {/* Engagement rail */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-6 z-10" data-testid="detail-engagement-rail">
            <button data-testid="detail-like-btn" onClick={() => engage("like", "likes")} className={railBtn}>
              <ThumbsUp size={20} className={reacted.like ? "text-blue-400" : ""} fill={reacted.like ? "currentColor" : "none"} /> Like
            </button>
            <button data-testid="detail-heart-btn" onClick={() => engage("heart", "hearts")} className={railBtn}>
              <Heart size={20} className={reacted.heart ? "text-red-500" : ""} fill={reacted.heart ? "currentColor" : "none"} /> Heart
            </button>
            <button data-testid="detail-save-btn" onClick={onBookmark} className={railBtn}>
              <Bookmark size={20} className={isSaved ? "text-[#F5B301]" : ""} fill={isSaved ? "currentColor" : "none"} /> Save
            </button>
            <button data-testid="detail-share-btn" onClick={share} className={railBtn}>
              <Share2 size={20} /> Share
            </button>
          </div>
        </div>

        {/* INFO CARD */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:absolute lg:inset-x-0 lg:top-10">
          <div className="lg:ml-auto lg:mr-16 lg:w-[400px] -mt-10 lg:mt-0 relative z-10 rounded-2xl bg-[#0d0d0d]/95 backdrop-blur-xl border border-white/10 p-6 text-left" data-testid="detail-info-card">
            <p className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#F5B301] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F5B301] live-dot" /> Live Opportunity
              {pitch.trending && <span className="flex items-center gap-1 normal-case tracking-normal text-black bg-gradient-to-r from-[#F5B301] to-orange-500 px-2 py-0.5 rounded-full trending-badge"><Flame size={9} fill="black" /> Trending</span>}
            </p>
            <h2 className="mt-3 font-heading font-bold text-3xl text-white leading-tight">{pitch.company}</h2>
            <p className="mt-1.5 text-xs text-zinc-400">{pitch.sector.split("•").map((s) => s.trim()).join("  ·  ")}</p>
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{pitch.tagline} — {pitch.deck.solution.split(".")[0]}.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {d.badges.map((b) => (
                <span key={b} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-200 border border-white/15 rounded-full px-3 py-1">
                  {b === "Verified" ? <BadgeCheck size={11} className="text-[#F5B301]" /> : b === "Reviewed" ? <Star size={11} className="text-[#F5B301]" /> : <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />}
                  {b}
                </span>
              ))}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 border-t border-white/10 pt-4">
              <div>
                <p className="font-heading font-bold text-lg text-white">{pitch.deck.ask}</p>
                <p className="text-[11px] text-zinc-500">Raising Now</p>
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-[#F5B301]">{pitch.score} / 10</p>
                <p className="text-[11px] text-zinc-500">EBN Score</p>
              </div>
            </div>
            <button
              data-testid="express-interest-btn"
              onClick={expressInterest}
              className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#F5B301] text-black text-sm font-bold uppercase tracking-wide hover:bg-[#D99E01] transition-colors"
            >
              <CircleDollarSign size={16} /> Express Capital Interest
            </button>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button data-testid="meet-founder-btn" onClick={() => toast.info(`Intro request sent to ${pitch.founder} (demo)`)} className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-white/15 text-xs font-semibold text-white hover:border-[#F5B301]/50 hover:text-[#F5B301] transition-colors">
                <UserRound size={13} /> Meet Founder
              </button>
              <button data-testid="open-dataroom-btn" onClick={() => { setTab("dataroom"); document.getElementById("detail-tabs")?.scrollIntoView({ behavior: "smooth" }); }} className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-white/15 text-xs font-semibold text-white hover:border-[#F5B301]/50 hover:text-[#F5B301] transition-colors">
                <FolderOpen size={13} /> Open Data Room
              </button>
            </div>
            <div className="mt-4 flex lg:hidden items-center justify-around border-t border-white/10 pt-3 text-xs text-zinc-400">
              <button onClick={() => engage("like", "likes")} className="flex items-center gap-1.5"><ThumbsUp size={14} /> {counts.likes}</button>
              <button onClick={() => engage("heart", "hearts")} className="flex items-center gap-1.5"><Heart size={14} /> {counts.hearts}</button>
              <button onClick={onBookmark} className="flex items-center gap-1.5"><Bookmark size={14} fill={isSaved ? "currentColor" : "none"} /> Save</button>
              <button onClick={share} className="flex items-center gap-1.5"><Share2 size={14} /> Share</button>
            </div>
          </div>
        </div>
      </section>

      {/* TABS */}
      <section id="detail-tabs" className="max-w-7xl mx-auto px-4 sm:px-6 mt-12 lg:mt-16">
        <div className="flex gap-1 overflow-x-auto hide-scrollbar border-b border-white/10" data-testid="detail-tab-bar">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`detail-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 flex items-center gap-2 px-5 py-3.5 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#F5B301] text-[#F5B301]" : "border-transparent text-zinc-500 hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
              {t.locked && !premium && <Lock size={10} className="text-zinc-600" />}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "story" && (
            <div data-testid="tab-content-story">
              <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-2">
                {d.chapters.map((c, i) => (
                  <button
                    key={c.title}
                    data-testid={`chapter-card-${i}`}
                    onClick={() => selectChapter(i)}
                    className={`shrink-0 w-60 rounded-xl overflow-hidden border text-left transition-colors ${
                      i === chapter ? "border-[#F5B301]" : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className="relative aspect-video">
                      <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/35" />
                      {i === chapter && <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider text-[#F5B301]">Now Playing</span>}
                      <span className="absolute bottom-2 left-2 w-7 h-7 rounded-full bg-black/60 border border-white/25 flex items-center justify-center">
                        <Play size={11} className="text-[#F5B301] ml-0.5" fill="#F5B301" />
                      </span>
                    </div>
                    <div className="p-3 bg-[#0d0d0d]">
                      <p className="text-sm font-semibold text-white">{c.title}</p>
                      <p className="text-[11px] text-zinc-500">{c.duration}</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-8 max-w-3xl text-left space-y-5">
                <SectionBlock title="The Problem">{pitch.deck.problem}</SectionBlock>
                <SectionBlock title="The Solution">{pitch.deck.solution}</SectionBlock>
              </div>
            </div>
          )}

          {tab === "business" && (premium ? (
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl" data-testid="tab-content-business">
              <SectionBlock title="Business Model">{d.business.model}</SectionBlock>
              <SectionBlock title="Market Opportunity">{d.business.market}</SectionBlock>
              <SectionBlock title="Go-To-Market">{d.business.gtm}</SectionBlock>
              <SectionBlock title="Competitive Landscape">{d.business.competition}</SectionBlock>
              <SectionBlock title="Problem">{d.business.problem}</SectionBlock>
              <SectionBlock title="Solution">{d.business.solution}</SectionBlock>
            </div>
          ) : <PremiumLock label="The Business deep-dive" />)}

          {tab === "traction" && (premium ? (
            <div data-testid="tab-content-traction">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {d.traction.kpis.map((k) => (
                  <div key={k.label} className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-5 text-left">
                    <p className="font-heading font-bold text-3xl text-[#F5B301]">{k.value}</p>
                    <p className="text-xs text-zinc-500 mt-1">{k.label}</p>
                  </div>
                ))}
              </div>
              <div className="max-w-3xl space-y-5">
                <SectionBlock title="Traction Summary">{d.traction.summary}</SectionBlock>
                <SectionBlock title="Use of Funds">{pitch.deck.use_of_funds}</SectionBlock>
              </div>
            </div>
          ) : <PremiumLock label="Traction & metrics" />)}

          {tab === "trust" && (premium ? (
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl" data-testid="tab-content-trust">
              {d.trust.map((t) => (
                <div key={t.title} className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-6 text-left flex gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-[#F5B301]/10 border border-[#F5B301]/25 flex items-center justify-center">
                    <ShieldCheck size={18} className="text-[#F5B301]" />
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-white">{t.title}</h3>
                    <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <PremiumLock label="The Trust report" />)}

          {tab === "qna" && (premium ? (
            <div className="max-w-3xl space-y-6" data-testid="tab-content-qna">
              {d.qna.map((item) => (
                <div key={item.q} className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-6 text-left">
                  <p className="font-heading font-semibold text-white flex items-start gap-2.5">
                    <MessagesSquare size={16} className="text-[#F5B301] mt-0.5 shrink-0" /> {item.q}
                  </p>
                  <p className="text-sm text-zinc-400 mt-2.5 leading-relaxed pl-7">{item.a}</p>
                </div>
              ))}
            </div>
          ) : <PremiumLock label="Founder Q&A" />)}

          {tab === "dataroom" && (premium ? (
            <div className="max-w-3xl space-y-3" data-testid="tab-content-dataroom">
              {d.dataroom.map((f) => (
                <div key={f.name} className="rounded-xl bg-[#0d0d0d] border border-white/5 px-5 py-4 flex items-center gap-4 text-left">
                  <FileText size={18} className="text-[#F5B301] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{f.name}</p>
                    <p className="text-[11px] text-zinc-500">{f.type} · {f.size}</p>
                  </div>
                  <button
                    data-testid={`dataroom-download-${f.type.toLowerCase()}-btn`}
                    onClick={() => toast.info("Demo data room — sample documents are placeholders.")}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#F5B301] border border-[#F5B301]/40 rounded-full px-4 py-1.5 hover:bg-[#F5B301]/10 transition-colors"
                  >
                    <Download size={12} /> Download
                  </button>
                </div>
              ))}
            </div>
          ) : <PremiumLock label="The Data Room" />)}
        </div>
      </section>

      {/* MORE LIKE THIS */}
      {similar.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-4" data-testid="similar-pitches-section">
          <h2 className="text-2xl sm:text-3xl font-semibold text-left mb-8">More Like This</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10" data-testid="similar-pitches-grid">
            {similar.map((p) => (
              <PitchFlipCard key={p.id} pitch={p} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
